import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import TaskButton from "./TaskButton";

export default function DraggableTaskButton({ task, onUpdate, isAuthor }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : "auto",
    position: "relative",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        position: "relative",
        transition: "all 0.2s",
        width: "100%",
        backgroundColor: theme.palette.background.paper,
        display: "flex",
        alignItems: "center",
        borderRadius: theme.shape.borderRadius,
        "&:hover .drag-handle": {
          visibility: "visible",
          opacity: 1,
        },
      }}
    >
      {/* Drag handle with the drag listeners */}
      <Tooltip title="Drag to reorder">
        <IconButton
          className="drag-handle"
          sx={{
            cursor: "grab",
            visibility: isMobile ? "visible" : "hidden",
            opacity: isMobile ? 1 : 0.6,
            transition: "visibility 0.2s, opacity 0.2s",
            color: theme.palette.text.secondary,
            padding: "8px",
            "&:active": {
              cursor: "grabbing",
            },
            "@media (hover: none)": {
              visibility: "visible",
              opacity: 1,
            },
          }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator />
        </IconButton>
      </Tooltip>

      {/* Task button without drag attributes */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          touchAction: "auto", // Allow normal touch behavior
          ...(isMobile && {
            "&:active": {
              backgroundColor: theme.palette.action.hover,
            },
          }),
        }}
      >
        <TaskButton
          task={task}
          onUpdate={onUpdate}
          isAuthor={isAuthor}
          sx={{ flex: 1 }}
        />
      </Box>
    </Box>
  );
}
